(() => {
  const publish = (name, data) => {
    try {
      const analytics = window.Shopify?.analytics;
      if (typeof analytics?.publish === 'function') Promise.resolve(analytics.publish(name, data)).catch(() => {});
    } catch (_) { /* Analytics must never interrupt shopping. */ }
  };

  class AW26Purchase extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.form = this.closest('form');
      this.variants = JSON.parse(this.querySelector('[data-aw26-variants]').textContent);
      const { signal } = this.controller;
      this.form.addEventListener('variant:change', event => this.update(event.detail.variant), { capture: true, signal });
      // Guard native submissions as well as the theme's AJAX form.
      this.form.addEventListener('submit', event => {
        const state = this.variants[this.form.elements.namedItem('id')?.value]?.state;
        if (!state || state === 'sold-out') {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal });
      this.update({ id: this.form.elements.namedItem('id')?.value || this.dataset.variant });
      customElements.whenDefined('buy-buttons').then(() => {
        if (this.isConnected) this.update({ id: this.form.elements.namedItem('id')?.value || this.dataset.variant });
      });
    }

    disconnectedCallback() { this.controller?.abort(); }

    update(variant) {
      const entry = variant && this.variants[variant.id];
      const state = entry?.state || 'unavailable';
      const preorder = state === 'preorder';
      const disabled = state === 'sold-out' || state === 'unavailable';
      const label = preorder ? 'PRE-ORDER' : state === 'in-stock' ? this.dataset.addLabel : state === 'sold-out' ? this.dataset.soldLabel : this.dataset.unavailableLabel;
      document.querySelectorAll('buy-buttons').forEach(group => {
        // An input named "id" shadows HTMLFormElement.id in this theme.
        if (group.getAttribute('form') !== this.form.getAttribute('id')) return;
        group.classList.add('aw26-buy-buttons');
        const button = group.querySelector('button[type=submit]');
        if (!button) return;
        button.disabled = disabled;
        button.classList.toggle('button--subdued', disabled);
        button.classList.toggle('atc-button--preorder', preorder);
        const content = button.getAttribute('is') === 'custom-button' ? button.firstElementChild : button;
        if (content) content.textContent = label;
      });
      this.querySelector('[data-aw26-preorder]').hidden = !preorder;
      this.querySelector('[data-aw26-estimate]').textContent = entry?.estimate || '';
      const property = this.querySelector('[data-aw26-property]');
      property.disabled = !preorder;
      property.value = preorder ? `Production + Delivery: ${entry.estimate}` : '';
      const urgency = this.querySelector('[data-aw26-urgency]');
      if (urgency) urgency.hidden = state !== 'in-stock';
      const info = this.closest('.product-info');
      if (info) info.dataset.aw26State = state;
    }
  }

  const seenAccessories = new Set();
  // The theme updates totals and line items; keep its delivery banner in sync too.
  document.addEventListener('cart:change', event => {
    if (!document.body.hasAttribute('data-aw26-enabled')) return;
    const html = Object.values(event.detail?.cart?.sections || {}).find(value => typeof value === 'string' && value.includes('cart-rotating-usps'));
    if (!html) return;
    const banner = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-rotating-usps');
    if (banner) requestAnimationFrame(() => document.querySelector('cart-drawer cart-rotating-usps')?.replaceWith(banner));
  });
  class AW26Accessories extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.addEventListener('submit', event => this.add(event), { signal });
      this.addEventListener('change', event => {
        const select = event.target.closest('select[name=id]');
        if (select) select.closest('form').querySelector('[data-aw26-price]').textContent = select.selectedOptions[0].dataset.price;
      }, { signal });
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const form = entry.target;
          if (!entry.isIntersecting || !this.closest('cart-drawer')?.open || seenAccessories.has(form.dataset.productId)) return;
          seenAccessories.add(form.dataset.productId);
          publish('aw26_accessory_viewed', { product_id: form.dataset.productId, placement: 'cart', campaign: 'AW26' });
        });
      }, { threshold: .5 });
      this.querySelectorAll('[data-aw26-accessory]').forEach(form => this.observer.observe(form));
      // Opening an already laid-out drawer must also trigger visibility evaluation.
      this.closest('cart-drawer')?.addEventListener('dialog:after-show', () => {
        this.querySelectorAll('[data-aw26-accessory]').forEach(form => { this.observer.unobserve(form); this.observer.observe(form); });
      }, { signal });
    }

    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }

    async add(event) {
      const form = event.target.closest('[data-aw26-accessory]');
      if (!form) return;
      event.preventDefault();
      if (this.busy) return;
      this.busy = true;
      const buttons = [...this.querySelectorAll('button[type=submit]')];
      const status = this.querySelector('[role=status]');
      buttons.forEach(button => { button.disabled = true; });
      form.setAttribute('aria-busy', 'true');
      status.textContent = 'Adding to your order…';
      const root = window.Shopify?.routes?.root || '/';
      const sections = [this.dataset.section];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      const data = new FormData(form);
      data.set('sections', [...new Set(sections)].slice(0, 5).join(','));
      data.set('sections_url', window.location.pathname);
      let added = false;
      try {
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', body: data, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'This item could not be added. Please try again.');
        added = true;
        publish('aw26_accessory_added', { product_id: form.dataset.productId, variant_id: data.get('id'), quantity: 1, placement: 'cart', campaign: 'AW26' });
        window.maisonTrackUpsellAdd?.('cart');
        status.textContent = `${form.dataset.productTitle} added to your order.`;
        form.querySelector('button').textContent = 'ADDED';
        const cartResponse = await fetch(`${root}cart.js`);
        if (!cartResponse.ok) throw new Error('Cart refresh failed');
        const cart = await cartResponse.json();
        cart.sections = result.sections;
        if (!cart.sections?.[this.dataset.section]) throw new Error('Cart section unavailable');
        // Uses the existing cart test's section replacement and keeps the drawer open.
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
      } catch (error) {
        if (added) {
          status.textContent = 'Added to your order. Open your cart to see the updated total. ';
          const link = document.createElement('a');
          link.href = `${root}cart`;
          link.textContent = 'View cart';
          status.append(link);
        } else {
          status.textContent = error.message || 'Unable to add this item. Please try again.';
        }
      } finally {
        this.busy = false;
        form.removeAttribute('aria-busy');
        buttons.forEach(button => { button.disabled = added && button.form === form; });
      }
    }
  }
  if (!customElements.get('aw26-purchase')) customElements.define('aw26-purchase', AW26Purchase);
  if (!customElements.get('aw26-accessories')) customElements.define('aw26-accessories', AW26Accessories);
})();
